import { IModalStyleProps, IModalStyles, IStackStyles, IStyleFunctionOrObject, ITheme } from '@fluentui/react';
import { IAppContext } from 'App/AppContext';
import { IPIconButton, IPTooltipHost } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { TFunction } from 'i18next';
import Task from 'models/tasks/task';
import { useEffect, useRef, useState } from 'react';
import { LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import { TaskContext } from '../Context/TaskContext';

export const taskModalWidthTransitionMs = 600;

export const onRenderTaskContext = (item: Task): JSX.Element => {
  return (
    <IPStack verticalFill styles={{ root: { width: 400, height: 400, padding: 20 } }}>
      <TaskContext task={item} orgTask={item} allowEdit={false} hideHeaders={true} navigateExternal={false} />
    </IPStack>
  );
};

export const getTaskViewModalStyles = (
  windowLevel: number | undefined,
  fullWindow?: boolean,
): IStyleFunctionOrObject<IModalStyleProps, IModalStyles> | undefined => {
  if (fullWindow) {
    return {
      main: {
        width: '100vw',
        height: '100vh',
        maxWidth: '100vw',
        maxHeight: '100vh',
        margin: 0,
        top: 0,
        left: 0,
        borderRadius: 0,
      },
    };
  }

  const offset = windowLevel ? windowLevel * 20 : undefined;

  if (offset) {
    return {
      main: {
        left: offset,
        top: offset,
      },
    };
  } else {
    return undefined;
  }
};

export const getTaskViewModalStackStylesFormMode = (
  theme: ITheme,
  isMobile: boolean,
  useDarkMode: boolean,
): IStackStyles | undefined => {
  return {
    root: {
      position: 'absolute',
      left: 0,
      top: 0,
      height: '90vh', //based on manual check: should be 92vh theoretically?
      minHeight: 516,
      width: '96vw',
      minWidth: isMobile ? '92vw' : 800,
      maxWidth: 1400,
      padding: '20px',
      background: useDarkMode ? theme.palette.neutralDark : theme.palette.white,
      zIndex: 999999999,
    },
  };
};

// A side pane (the AI task assistant) adds a third column: the modal takes more of the viewport and
// its ceiling moves up, or the pane would squeeze the two task columns below their minimum.
export const getTaskViewModalStackStyles = (
  isMobile: boolean,
  isCompact: boolean | undefined,
  hasSidePane?: boolean,
  fullWindow?: boolean,
  // Fluent's own modal margin (16px around, its default max height of calc(100% - 32px)) instead of 92vh
  fluentMargin?: boolean,
): IStackStyles | undefined => {
  const height = fluentMargin ? 'calc(100vh - 32px)' : '92vh';

  if (fullWindow) {
    return {
      root: {
        height: '100vh',
        width: '100vw',
        padding: '20px',
      },
    };
  }

  if (hasSidePane) {
    return {
      root: {
        height,
        width: isCompact ? '70vw' : '96vw',
        minWidth: isMobile ? '92vw' : isCompact ? 760 : 1100,
        maxWidth: 1900,
        padding: '20px',
        transition: `width ${taskModalWidthTransitionMs}ms`,
      },
    };
  }

  return {
    root: {
      height,
      width: isCompact ? '46vw' : '92vw',
      minWidth: isMobile ? '92vw' : isCompact ? 400 : 800,
      maxWidth: 1400,
      padding: '20px',
      transition: `width ${taskModalWidthTransitionMs}ms`,
    },
  };
};

// To keep the width animation while avoiding the stuck menu, this hook returns a key that changes once
// the animation has settled. Applying it to the Pivot remounts it, forcing a fresh overflow calculation
// at the final width (all tabs visible, valid offsets).
export const useCompactViewPivotRemountKey = (isCompact: boolean | undefined): number => {
  const [remountKey, setRemountKey] = useState<number>(0);
  const prevIsCompact = useRef<boolean | undefined>(isCompact);

  useEffect(() => {
    const prev = prevIsCompact.current;
    prevIsCompact.current = isCompact;

    // Only react to an actual collapse/expand toggle (a change between two known states), which is what
    // triggers the width animation. Skips the initial render and any undefined -> defined initialization.
    if (prev === undefined || isCompact === undefined || prev === isCompact) {
      return;
    }

    const timer = setTimeout(() => setRemountKey((key) => key + 1), taskModalWidthTransitionMs + 50);

    return () => clearTimeout(timer);
  }, [isCompact]);

  return remountKey;
};

export const getCompactViewSwitch = (
  task: Task,
  isMobile: boolean,
  isCompact: boolean | undefined,
  t: TFunction<string[]>,
  setCompactView: (newValue: boolean) => void,
  appContext: IAppContext | undefined,
): JSX.Element | null => {
  if (isMobile) return null;

  return (
    <IPStack.Item>
      <IPTooltipHost content={isCompact ? t('task:CompactView.Expand') : t('task:CompactView.Collapse')}>
        <IPIconButton
          iconProps={{ iconName: isCompact ? 'DoubleChevronLeft8' : 'DoubleChevronRight8' }}
          onClick={() => {
            const newCompact = !isCompact;
            setCompactView(newCompact);
            setLocalStorageData(appContext, LocalStorageKeys.TaskCompactView, newCompact ? '1' : '0');
          }}
          styles={{ root: { height: 45 } }}
        ></IPIconButton>
      </IPTooltipHost>
    </IPStack.Item>
  );
};
